import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import useAuth from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import './Vendors.css';

const Vendors = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  // MFA states
  const [mfaStatus, setMfaStatus] = useState('loading');
  const [setupData, setSetupData] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [verifiedCode, setVerifiedCode] = useState(null);

  // Vendor states
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [copiedField, setCopiedField] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

  useEffect(() => {
    if (!hasRole('admin')) {
      navigate('/hub');
      return;
    }
    checkMfaStatus();
  }, [hasRole, navigate]);

  const checkMfaStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/mfa/status`, {
        withCredentials: true
      });
      if (response.data.totpEnabled) {
        setMfaStatus('verify');
      } else {
        setMfaStatus('setup');
      }
    } catch (error) {
      console.error('Error checking MFA status:', error);
      setMfaError('Failed to check MFA status');
      setMfaStatus('error');
    }
  };

  const startSetup = async () => {
    try {
      setMfaError('');
      const response = await axios.post(`${API_BASE}/mfa/setup`, {}, {
        withCredentials: true
      });
      setSetupData(response.data);
    } catch (error) {
      console.error('Error starting MFA setup:', error);
      setMfaError(error.response?.data?.error || 'Failed to start MFA setup');
    }
  };

  const verifySetup = async () => {
    try {
      setMfaError('');
      if (!totpCode || totpCode.length !== 6) {
        setMfaError('Please enter a 6-digit code');
        return;
      }
      await axios.post(`${API_BASE}/mfa/verify-setup`, { code: totpCode }, {
        withCredentials: true
      });
      setVerifiedCode(totpCode);
      setMfaStatus('verified');
      fetchVendors(totpCode);
    } catch (error) {
      console.error('Error verifying MFA setup:', error);
      setMfaError(error.response?.data?.error || 'Invalid verification code');
      setTotpCode('');
    }
  };

  const verifyCode = async () => {
    try {
      setMfaError('');
      if (!totpCode || totpCode.length !== 6) {
        setMfaError('Please enter a 6-digit code');
        return;
      }
      await axios.post(`${API_BASE}/mfa/verify`, { code: totpCode }, {
        withCredentials: true
      });
      setVerifiedCode(totpCode);
      setMfaStatus('verified');
      fetchVendors(totpCode);
    } catch (error) {
      console.error('Error verifying MFA code:', error);
      setMfaError(error.response?.data?.error || 'Invalid verification code');
      setTotpCode('');
    }
  };

  const fetchVendors = useCallback(async (code) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/vendors`, {
        withCredentials: true,
        headers: {
          'X-TOTP-Code': code
        }
      });
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      if (error.response?.status === 403) {
        setMfaError('Session expired. Please enter your code again.');
        setMfaStatus('verify');
        setVerifiedCode(null);
        setTotpCode('');
      } else {
        setMfaError('Failed to fetch vendors');
      }
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  const togglePasswordVisibility = (vendorId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [vendorId]: !prev[vendorId]
    }));
  };

  const copyToClipboard = async (text, fieldId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setTotpCode(value);
    setMfaError('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && totpCode.length === 6) {
      if (mfaStatus === 'setup' && setupData) {
        verifySetup();
      } else if (mfaStatus === 'verify') {
        verifyCode();
      }
    }
  };

  // Filter vendors based on search
  const filteredVendors = vendors.filter(vendor =>
    vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (vendor.username && vendor.username.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!hasRole('admin')) {
    return null;
  }

  // Loading MFA status
  if (mfaStatus === 'loading') {
    return (
      <div className="vendors-container">
        <div className="loading">Checking authentication...</div>
      </div>
    );
  }

  // MFA Setup Required
  if (mfaStatus === 'setup') {
    return (
      <div className="vendors-container">
        <div className="mfa-setup">
          <h1>Two-Factor Authentication Required</h1>
          <p className="mfa-description">
            To access vendor credentials, you must set up two-factor authentication.
          </p>

          {!setupData ? (
            <div className="mfa-start">
              <p>You'll need an authenticator app like:</p>
              <ul className="app-list">
                <li>Google Authenticator</li>
                <li>Authy</li>
                <li>Microsoft Authenticator</li>
              </ul>
              <button className="btn-primary" onClick={startSetup}>
                Begin Setup
              </button>
            </div>
          ) : (
            <div className="mfa-qr-section">
              <h3>Step 1: Scan QR Code</h3>
              <p>Open your authenticator app and scan this QR code:</p>
              <div className="qr-container">
                <img src={setupData.qrCode} alt="TOTP QR Code" className="qr-code" />
              </div>
              <div className="manual-entry">
                <p>Can't scan? Enter this code manually:</p>
                <code className="secret-code">{setupData.secret}</code>
              </div>

              <h3>Step 2: Enter Verification Code</h3>
              <p>Enter the 6-digit code from your authenticator app:</p>
              <div className="code-input-section">
                <input
                  type="text"
                  className="totp-input"
                  value={totpCode}
                  onChange={handleCodeChange}
                  onKeyPress={handleKeyPress}
                  placeholder="000000"
                  maxLength="6"
                  autoFocus
                />
                <button
                  className="btn-primary"
                  onClick={verifySetup}
                  disabled={totpCode.length !== 6}
                >
                  Verify & Complete Setup
                </button>
              </div>
              {mfaError && <div className="mfa-error">{mfaError}</div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // MFA Verification Required
  if (mfaStatus === 'verify') {
    return (
      <div className="vendors-container">
        <div className="mfa-verify">
          <div className="mfa-modal">
            <div className="mfa-modal-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <h2>Authentication Required</h2>
            <p>Enter your 6-digit code from your authenticator app to access vendor credentials.</p>
            <div className="code-input-section">
              <input
                type="text"
                className="totp-input"
                value={totpCode}
                onChange={handleCodeChange}
                onKeyPress={handleKeyPress}
                placeholder="000000"
                maxLength="6"
                autoFocus
              />
              <button
                className="btn-primary"
                onClick={verifyCode}
                disabled={totpCode.length !== 6}
              >
                Verify
              </button>
            </div>
            {mfaError && <div className="mfa-error">{mfaError}</div>}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (mfaStatus === 'error') {
    return (
      <div className="vendors-container">
        <div className="mfa-error-page">
          <h2>Authentication Error</h2>
          <p>{mfaError}</p>
          <button className="btn-primary" onClick={() => window.location.reload()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Verified - show vendors
  if (loading) {
    return (
      <div className="vendors-container">
        <div className="loading">Loading vendors...</div>
      </div>
    );
  }

  return (
    <div className="vendors-container">
      <div className="vendors-header">
        <div className="header-top">
          <h1>Vendor Portal Credentials</h1>
          <div className="mfa-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="M9 12l2 2 4-4"/>
            </svg>
            MFA Verified
          </div>
        </div>
        <div className="search-bar">
          <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>
        <div className="vendor-count">
          {filteredVendors.length} of {vendors.length} vendors
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="vendors-table-container">
        <table className="vendors-table">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Username</th>
              <th>Password</th>
              <th>Portal</th>
            </tr>
          </thead>
          <tbody>
            {filteredVendors.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-results">
                  No vendors found matching "{searchTerm}"
                </td>
              </tr>
            ) : (
              filteredVendors.map((vendor) => (
                <tr key={vendor.id}>
                  <td className="vendor-name">{vendor.name}</td>
                  <td>
                    {vendor.username ? (
                      <div className="credential-cell">
                        <code>{vendor.username}</code>
                        <button
                          className="btn-copy"
                          onClick={() => copyToClipboard(vendor.username, `user-${vendor.id}`)}
                          title="Copy username"
                        >
                          {copiedField === `user-${vendor.id}` ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    ) : (
                      <span className="no-value">-</span>
                    )}
                  </td>
                  <td>
                    {vendor.password ? (
                      <div className="credential-cell">
                        <code>
                          {visiblePasswords[vendor.id] ? vendor.password : '••••••••'}
                        </code>
                        <button
                          className="btn-copy"
                          onClick={() => togglePasswordVisibility(vendor.id)}
                          title={visiblePasswords[vendor.id] ? 'Hide' : 'Show'}
                        >
                          {visiblePasswords[vendor.id] ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                              <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          )}
                        </button>
                        <button
                          className="btn-copy"
                          onClick={() => copyToClipboard(vendor.password, `pass-${vendor.id}`)}
                          title="Copy password"
                        >
                          {copiedField === `pass-${vendor.id}` ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    ) : (
                      <span className="no-value">-</span>
                    )}
                  </td>
                  <td>
                    {vendor.url ? (
                      <a
                        href={vendor.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="portal-link"
                      >
                        Open Portal
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                          <polyline points="15 3 21 3 21 9"/>
                          <line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                      </a>
                    ) : (
                      <span className="no-value">-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="vendors-mobile-list">
        {filteredVendors.length === 0 ? (
          <div className="no-results" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
            No vendors found matching "{searchTerm}"
          </div>
        ) : (
          filteredVendors.map((vendor) => (
            <div key={vendor.id} className="vendor-card">
              <div className="vendor-card-header">
                <span className="vendor-card-name">{vendor.name}</span>
                {vendor.url && (
                  <a
                    href={vendor.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="portal-link"
                  >
                    Open
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </a>
                )}
              </div>
              {vendor.username && (
                <div className="vendor-card-row">
                  <span className="vendor-card-label">Username</span>
                  <div className="vendor-card-value">
                    <code>{vendor.username}</code>
                    <button
                      className="btn-copy"
                      onClick={() => copyToClipboard(vendor.username, `user-m-${vendor.id}`)}
                    >
                      {copiedField === `user-m-${vendor.id}` ? '✓' : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}
              {vendor.password && (
                <div className="vendor-card-row">
                  <span className="vendor-card-label">Password</span>
                  <div className="vendor-card-value">
                    <code>{visiblePasswords[vendor.id] ? vendor.password : '••••••••'}</code>
                    <button
                      className="btn-copy"
                      onClick={() => togglePasswordVisibility(vendor.id)}
                    >
                      {visiblePasswords[vendor.id] ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                    <button
                      className="btn-copy"
                      onClick={() => copyToClipboard(vendor.password, `pass-m-${vendor.id}`)}
                    >
                      {copiedField === `pass-m-${vendor.id}` ? '✓' : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Vendors;
